import React from 'react';
import { CheckCircleIcon, XIcon, BookmarkIcon, ClockIcon } from './icons/InterfaceIcons';

interface TestSummaryProps {
    questions: any[];
    statuses: Map<number, string>;
    answers: Map<number, string>;
    timeLeft: number;
    onClose: () => void;
    onSubmit: () => void;
    onNavigateToQuestion: (index: number) => void;
}

export const TestSummary: React.FC<TestSummaryProps> = ({ 
    questions, 
    statuses, 
    answers, 
    timeLeft, 
    onClose, 
    onSubmit, 
    onNavigateToQuestion 
}) => {
    const formatTime = (seconds: number) => {
        const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
        const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${h}:${m}:${s}`;
    };

    const getStats = () => {
        const answered = questions.filter((_, i) => ['answered', 'answeredAndMarked'].includes(statuses.get(i) || '')).length;
        const marked = questions.filter((_, i) => ['marked', 'answeredAndMarked'].includes(statuses.get(i) || '')).length;
        const unanswered = questions.length - answered;
        
        return { answered, marked, unanswered };
    };

    const stats = getStats();

    const getQuestionsByStatus = (status: string) => {
        return questions.map((_, index) => ({ index, status: statuses.get(index) }))
            .filter(({ status: qStatus }) => {
                switch (status) {
                    case 'answered':
                        return ['answered', 'answeredAndMarked'].includes(qStatus || '');
                    case 'marked':
                        return ['marked', 'answeredAndMarked'].includes(qStatus || '');
                    case 'unanswered':
                        return ['notAnswered'].includes(qStatus || '');
                    default:
                        return false;
                }
            });
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-brand-surface rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="p-6 border-b border-brand-border">
                    <div className="flex items-center justify-between">
                        <h2 className="text-2xl font-bold text-brand-text-primary">Test Summary</h2>
                        <div className="flex items-center space-x-4">
                            <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-mono ${
                                timeLeft <= 300 ? 'bg-red-100 text-red-700' : 
                                timeLeft <= 900 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-brand-bg text-brand-text-primary'
                            }`}>
                                <ClockIcon className="w-5 h-5" />
                                <span>{formatTime(timeLeft)}</span>
                            </div>
                            <button 
                                onClick={onClose}
                                className="text-brand-text-secondary hover:text-brand-text-primary"
                            >
                                <XIcon className="w-6 h-6" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Stats Overview */}
                <div className="p-6 border-b border-brand-border">
                    <div className="grid grid-cols-3 gap-6">
                        <div className="text-center">
                            <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mx-auto mb-2">
                                <CheckCircleIcon className="w-8 h-8 text-green-600" />
                            </div>
                            <div className="text-2xl font-bold text-green-600">{stats.answered}</div>
                            <div className="text-sm text-brand-text-secondary">Answered</div>
                        </div>
                        <div className="text-center">
                            <div className="flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mx-auto mb-2">
                                <BookmarkIcon className="w-8 h-8 text-purple-600" />
                            </div>
                            <div className="text-2xl font-bold text-purple-600">{stats.marked}</div>
                            <div className="text-sm text-brand-text-secondary">Marked</div>
                        </div>
                        <div className="text-center">
                            <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-2">
                                <XIcon className="w-8 h-8 text-red-600" />
                            </div>
                            <div className="text-2xl font-bold text-red-600">{stats.unanswered}</div>
                            <div className="text-sm text-brand-text-secondary">Unanswered</div>
                        </div>
                    </div>
                </div>

                {/* Question Lists */}
                <div className="p-6 max-h-96 overflow-y-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Answered Questions */}
                        <div>
                            <h3 className="font-semibold text-green-600 mb-3 flex items-center">
                                <CheckCircleIcon className="w-5 h-5 mr-2" />
                                Answered ({stats.answered})
                            </h3>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {getQuestionsByStatus('answered').map(({ index }) => (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            onNavigateToQuestion(index);
                                            onClose();
                                        }}
                                        className="w-full text-left p-2 rounded bg-green-50 hover:bg-green-100 border border-green-200 transition-colors"
                                    >
                                        <span className="text-sm font-medium">Question {index + 1}</span>
                                        {statuses.get(index) === 'answeredAndMarked' && (
                                            <BookmarkIcon className="w-4 h-4 text-purple-500 float-right mt-0.5" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Marked Questions */}
                        <div>
                            <h3 className="font-semibold text-purple-600 mb-3 flex items-center">
                                <BookmarkIcon className="w-5 h-5 mr-2" />
                                Marked ({stats.marked})
                            </h3>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {getQuestionsByStatus('marked').map(({ index }) => (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            onNavigateToQuestion(index);
                                            onClose();
                                        }}
                                        className="w-full text-left p-2 rounded bg-purple-50 hover:bg-purple-100 border border-purple-200 transition-colors"
                                    >
                                        <span className="text-sm font-medium">Question {index + 1}</span>
                                        {statuses.get(index) === 'answeredAndMarked' && (
                                            <CheckCircleIcon className="w-4 h-4 text-green-500 float-right mt-0.5" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Unanswered Questions */}
                        <div>
                            <h3 className="font-semibold text-red-600 mb-3 flex items-center">
                                <XIcon className="w-5 h-5 mr-2" />
                                Unanswered ({stats.unanswered})
                            </h3>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {getQuestionsByStatus('unanswered').map(({ index }) => (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            onNavigateToQuestion(index);
                                            onClose();
                                        }}
                                        className="w-full text-left p-2 rounded bg-red-50 hover:bg-red-100 border border-red-200 transition-colors"
                                    >
                                        <span className="text-sm font-medium">Question {index + 1}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-brand-border bg-brand-bg">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                        <div className="text-sm text-brand-text-secondary">
                            Progress: {stats.answered}/{questions.length} questions answered
                        </div>
                        <div className="flex space-x-3">
                            <button 
                                onClick={onClose}
                                className="px-6 py-2 text-sm font-semibold rounded-lg border border-brand-border hover:bg-brand-surface transition-colors"
                            >
                                Continue Test
                            </button>
                            <button 
                                onClick={onSubmit}
                                className="px-6 py-2 text-sm font-semibold text-white bg-brand-accent-red hover:bg-red-700 rounded-lg transition-colors"
                            >
                                Submit Test
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};